import { Col, Row } from 'antd';
import Text from 'antd/lib/typography/Text';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { IApplicationState } from '../../store';
import { actionCreators as PurchaseTypeActions, PurchaseTypes } from '../../store/PurchaseTypes';
import ZLButton, { ButtonSize, ButtonType } from '../zl-button/zl-button';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import { Routes, getCurrentRoute, navigateTo } from '../../helpers/routes';
import { actionCreators as SessionActions, ILoadOpenDatesParams } from '../../store/Sessions';
import './purchase-type.scss';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import { DISCOUNT_TIERS, CAMPAIGN_CONFIG } from '../tiered-group-size-discount/tieredGroupSizeDiscountConfig';
import { blackFridayPromo, isFeatureEnabled, KnownFlag } from '../../store/featureFlags';


export interface IPurchaseTypeProps {
    onClick?: (num: number) => void;
}

const PurchaseType: React.FunctionComponent<IPurchaseTypeProps> = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const globalWebsiteUrl = useSelector((state: IApplicationState) => state.appSettings.globalWebsiteUrl);
    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const selectedPurchaseTypeUI = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseTypeUI);
    const siteUrlKey = useSelector((state: IApplicationState) => state.sites.selectedSite?.urlKey);
    const privateEventsAvailable = useSelector(
        (state: IApplicationState) => state.sites.bookingOptions?.privateEventsAvailable
    );
    const selectedSite = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const bookingOptions = useSelector((state: IApplicationState) => state.sites.bookingOptions);
    const accessCode = useSelector((state: IApplicationState) => state.sites.accessCode);
    const selectedGame = useSelector((state: IApplicationState) => state.games.selectedGame);
    const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
    const selectedPlayers = useSelector((state: IApplicationState) => state.players.selectedPlayers);

    const discountPercentage = useSelector(
        (state: IApplicationState) => state.giftVoucher.configDetails?.activeDiscount?.discountPercentage
    );
    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);

    const tieredGroupSizeDiscountEnabled = CAMPAIGN_CONFIG.enabled;

    const highlightPriceEnabled = useSelector((state: IApplicationState) => state.sites.bookingOptions?.usePriceTierHighlighting) ?? false;
    const priceBucketsEnabled = useMemo(() => {
        return highlightPriceEnabled && selectedPurchaseType === PurchaseTypes.Booking
    }, [highlightPriceEnabled, selectedPurchaseType]);

    const featureFlags = useSelector((state: IApplicationState) => state.featureFlags);
    const blackFridayEnabled = isFeatureEnabled(featureFlags, KnownFlag.blackFriday);
    const blackFridayDiscountLabel = t('giftVoucherSection.purchase.blackFridayPromoCode', blackFridayPromo);

    const isLoading = selectedSite?.siteId !== bookingOptions?.siteId;
    const pathName = location.pathname;

    const bookingsPackageType = 1;
    const privateEventPackageType = 2;



    const link =
        selectedSite?.groupBookingUrl !== undefined &&
        selectedSite?.groupBookingUrl !== '#' &&
        selectedSite?.groupBookingUrl !== ''
            ? selectedSite.groupBookingUrl
            : `${globalWebsiteUrl}/${selectedLanguage.globalWebsiteLangCode || 'en'}/${selectedSite?.urlKey}/events`;

    useEffect(() => {
        if (selectedPurchaseType === PurchaseTypes.PrivateEvent && privateEventsAvailable === false) {
            dispatch(PurchaseTypeActions.selectPurchaseType(PurchaseTypes.Booking));
            dispatch(navigateTo(Routes.BookNow, selectedSite?.urlKey, location.search));
        }
    }, [dispatch, selectedLanguage.momentLocale, location.search, selectedSite?.urlKey, selectedPurchaseType, privateEventsAvailable]);

    useEffect(() => {
        const page = getCurrentRoute(pathName);
        if (page === Routes.BookNow && (selectedPurchaseType !== PurchaseTypes.Booking || !selectedPurchaseTypeUI)) {
            dispatch(PurchaseTypeActions.selectPurchaseType(PurchaseTypes.Booking));
        }
        if (
            page === Routes.GiftVoucher &&
            (selectedPurchaseType !== PurchaseTypes.GiftVoucher || !selectedPurchaseTypeUI)
        ) {
            dispatch(PurchaseTypeActions.selectPurchaseType(PurchaseTypes.GiftVoucher));
        }
        if (
            page === Routes.PrivateEvent &&
            (selectedPurchaseType !== PurchaseTypes.PrivateEvent || !selectedPurchaseTypeUI)
        ) {
            dispatch(PurchaseTypeActions.selectPurchaseType(PurchaseTypes.PrivateEvent));
        }
    }, [pathName, selectedPurchaseType, selectedPurchaseTypeUI, dispatch]);

    const buttonSelected = useCallback(
        (selectedType: PurchaseTypes) => {
            if (selectedType === PurchaseTypes.Booking && siteUrlKey) {
                dispatch(navigateTo(Routes.BookNow, siteUrlKey, location.search));
                if (selectedSite) {
                    dispatch(
                        SessionActions.loadOpenDates({
                            siteId: selectedSite.siteId,
                            accessCode: accessCode,
                            experienceId: selectedGame?.experienceId,
                            packageTypeId: bookingsPackageType,
                            playerCount: priceBucketsEnabled ? selectedPlayers : undefined
                        } as ILoadOpenDatesParams)
                    );
                } else {
                    console.log(`Error - purchase-type - unable to loadOpenDates due to null site`);
                }
            } else if (selectedType === PurchaseTypes.GiftVoucher && siteUrlKey) {
                dispatch(navigateTo(Routes.GiftVoucher, siteUrlKey, location.search));

                if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                    dispatch(BookingActions.expireBooking());
                    dispatch(SessionActions.clearSelectedDateTime());
                }
            } else if (selectedType === PurchaseTypes.PrivateEvent && siteUrlKey) {
                if (privateEventsAvailable) {
                    dispatch(navigateTo(Routes.PrivateEvent, siteUrlKey, location.search));
                    if (selectedSite) {
                        dispatch(
                            SessionActions.loadOpenDates({
                                siteId: selectedSite.siteId,
                                accessCode: accessCode,
                                experienceId: selectedGame?.experienceId,
                                packageTypeId: privateEventPackageType,
                                playerCount: priceBucketsEnabled ? selectedPlayers : undefined
                            } as ILoadOpenDatesParams)
                        );

                        if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                            dispatch(BookingActions.expireBooking());
                            dispatch(SessionActions.clearSelectedDateTime());
                        }
                    } else {
                        console.log(`Error - purchase-type - unable to loadOpenDates due to null site`);
                    }
                } else {
                    window.open(link, '_blank');
                    return;
                }
            }
            dispatch(PurchaseTypeActions.selectPurchaseType(selectedType));
        },
        [
            dispatch,
            siteUrlKey,
            privateEventsAvailable,
            link,
            selectedSite,
            accessCode,
            selectedGame,
            pendingBooking,
            bookingStatus,
            selectedPlayers,
            priceBucketsEnabled,
            location.search
        ]
    );

    if (isLoading) return <LoadingSpinner />;

    return (
        <React.Fragment>
            <Row justify="start" align="top" className="zl-purchase-type-container">
                <Col span={24}>
                    <ZLButton
                        type={ButtonType.Default}
                        selected={selectedPurchaseType === PurchaseTypes.Booking}
                        size={ButtonSize.Large}
                        disabled={false}
                        className="zl-purchase-type-button"
                        block
                        tall
                        onClick={() => buttonSelected(PurchaseTypes.Booking)}
                    >
                        <span className="zl-button-stack">
                            <Text>{t('purchaseTypes.bookingLabel')}</Text>

                            {blackFridayEnabled && (
                                <Text className="zl-discount-text">
                                    {blackFridayDiscountLabel}
                                </Text>
                            )}

                            {tieredGroupSizeDiscountEnabled && (
                                <Text className="zl-discount-text">
                                    {DISCOUNT_TIERS.map((tier, i) => (
                                        <React.Fragment key={tier.threshold}>
                                            {i > 0 && <br />}
                                            {t('tieredGroupSizeDiscount.purchaseTypeButton.tier', {
                                                discount: tier.label,
                                                threshold: tier.threshold,
                                            })}
                                        </React.Fragment>
                                    ))}
                                </Text>
                            )}
                        </span>
                    </ZLButton>
                    <ZLButton
                        type={ButtonType.Default}
                        selected={selectedPurchaseType === PurchaseTypes.GiftVoucher}
                        size={ButtonSize.Large}
                        disabled={false}
                        className="zl-purchase-type-button"
                        block
                        tall
                        onClick={() => buttonSelected(PurchaseTypes.GiftVoucher)}
                    >
                        <span className="zl-button-stack">
                            <Text>{t('purchaseTypes.giftVoucherLabel')}</Text>

                            {discountPercentage && (
                                <Text className="zl-discount-text">
                                    {t('giftVoucherSection.purchase.limitedTimeDeal', {
                                        percentage: discountPercentage * 100,
                                    })}
                                </Text>
                                )}
                         </span>
                    </ZLButton>
                    <ZLButton
                        type={ButtonType.Default}
                        selected={selectedPurchaseType === PurchaseTypes.PrivateEvent}
                        size={ButtonSize.Large}
                        disabled={!privateEventsAvailable && !selectedSite?.hasGroupBookings}
                        className="zl-purchase-type-button"
                        block
                        tall
                        onClick={() => buttonSelected(PurchaseTypes.PrivateEvent)}
                    >
                        <Text>{t('purchaseTypes.privateEventLabel')}</Text>
                    </ZLButton>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default PurchaseType;
